import { quoteFamily } from '../core/fonts.js';
import { S, clamp } from '../state/store.js';

/* TEXT AREA — word-wrapped, multi-style paragraph text on a flat canvas
   texture. Unlike 3D Text this is never traced: a paragraph can mix
   typefaces, weights, sizes and colours per character, which isn't
   representable in one extruded mesh anyway, and word-wrap is a layout
   problem a rasteriser already solves for free. Same "canvas → CanvasTexture
   → plane" idiom scene/geometry.js's buildTextEffects already uses for the
   shadow/glow bitmap.

   A paragraph is the unit that wraps; a run is the unit that's styled — that
   split is what makes wrapping tractable at all, and it's what a real word
   processor's own model looks like. */

const FX_MAX_PX = 4096;   // texture ceiling — a wide box at high output res must not blow up VRAM

function runFontCss(run, px){
  return (run.italic ? 'italic ' : '') + (run.weight||400) + ' ' + Math.max(1,px) + 'px ' +
    quoteFamily(run.font) + ', sans-serif';
}

/* Split a run's text into word/whitespace tokens, each carrying its own style
   pre-measured. Word-wrap operates on these rather than whole runs, so a
   line can start or end mid-run — a bold word followed immediately by a
   plain one on the same line is the ordinary case, not an edge case. */
function tokensOf(probe, runs, baseSizePx){
  const toks = [];
  for(const r of runs){
    if(!r || !r.text) continue;
    const px = baseSizePx * (r.size||100)/100;
    const font = runFontCss(r, px);
    probe.font = font;
    const parts = r.text.match(/\S+|\s+/g) || [];
    for(const part of parts){
      const m = probe.measureText(part);
      toks.push({
        text: part, font, color: r.color || '#ffffff', underline: !!r.underline,
        space: /^\s+$/.test(part),
        width: m.width,
        /* FONT metrics, not ink. actualBoundingBox* measures the glyphs in THIS
           token — so a line containing a descender was taller than one without,
           and a paragraph's leading changed from line to line depending on
           whether the words in it happened to have a y or a g. fontBoundingBox*
           is a property of the face at this size and is identical for every
           string, which is what makes the lines even. */
        asc:  m.fontBoundingBoxAscent  || px*.8,
        desc: m.fontBoundingBoxDescent || px*.25,
        px
      });
    }
  }
  return toks;
}

const LIST_INDENT_EM = 1.6;   // hanging indent for list paragraphs, in base-size units

/* Greedy word-wrap for one paragraph. `listIndex` is the number to print for
   an ordered-list paragraph; ignored otherwise. */
function layoutParagraph(probe, para, availWidth, baseSizePx, listIndex){
  const indent = para.list ? baseSizePx * LIST_INDENT_EM : 0;
  const wrapWidth = Math.max(1, availWidth - indent);
  const toks = tokensOf(probe, para.runs, baseSizePx);

  const lines = [];
  let cur = [], x = 0;
  const flush = () => {
    while(cur.length && cur[cur.length-1].space){ x -= cur[cur.length-1].width; cur.pop(); }
    const asc  = cur.length ? Math.max(...cur.map(t=>t.asc))  : baseSizePx*.8;
    const desc = cur.length ? Math.max(...cur.map(t=>t.desc)) : baseSizePx*.25;
    /* The largest TYPE SIZE on the line, which is what its leading is a
       multiple of — a line is as tall as the biggest thing set on it. */
    const size = cur.length ? Math.max(...cur.map(t=>t.px)) : baseSizePx;
    lines.push({ toks: cur, asc, desc, size, width: x, indent });
    cur = []; x = 0;
  };

  if(!toks.length){
    // a blank paragraph still takes a line, sized off its (empty) first run
    probe.font = runFontCss(para.runs[0] || {font:'Inter', weight:400}, baseSizePx);
    const m = probe.measureText('M');
    lines.push({ toks: [], asc: m.fontBoundingBoxAscent||baseSizePx*.8,
                 desc: m.fontBoundingBoxDescent||baseSizePx*.25,
                 size: baseSizePx, width: 0, indent });
  }else{
    for(const t of toks){
      if(!t.space && cur.length && x + t.width > wrapWidth) flush();
      cur.push({ ...t, dx: x + indent });
      x += t.width;
    }
    flush();
  }

  if(para.list && lines.length){
    const mr = para.runs[0] || { font:'Inter', weight:400, color:'#ffffff' };
    probe.font = runFontCss(mr, baseSizePx);
    const text = para.list === 'number' ? (listIndex + '.') : '•';
    lines[0].marker = { text, font: probe.font, color: mr.color || '#ffffff' };
  }
  return lines;
}

/**
 * Word-wrap every paragraph to a fixed pixel width.
 * @returns {{lines:object[], height:number}} `lines` carry pre-positioned,
 *          pre-measured tokens ready to draw; `height` is the total content
 *          height in px, no padding.
 */
export function layoutParagraphs(paragraphs, availWidthPx, baseSizePx, lineHeightFactor){
  const probe = document.createElement('canvas').getContext('2d');
  const out = [];
  let y = 0, listIndex = 0, prevList = null;

  for(const para of (paragraphs||[])){
    listIndex = para.list === 'number' ? (prevList === 'number' ? listIndex+1 : 1) : 0;
    prevList = para.list;

    const plines = layoutParagraph(probe, para, availWidthPx, baseSizePx, listIndex);
    const lh = para.lineHeight || lineHeightFactor;
    const wrapWidth = availWidthPx - (para.list ? baseSizePx*LIST_INDENT_EM : 0);

    plines.forEach((l, i)=>{
      /* Alignment: shift the line's tokens by the slack left over after its own
         width. Justify skips the paragraph's LAST line — flush-left there is
         the standard typographic treatment, not a leftover ragged edge. */
      const slack = Math.max(0, wrapWidth - l.width);
      if(para.align === 'justify' && i < plines.length-1 && l.toks.some(t=>t.space)){
        const spaces = l.toks.filter(t=>t.space).length;
        const extra = slack / spaces;
        let acc = 0;
        for(const t of l.toks){ t.dx += acc; if(t.space) acc += extra; }
      }else{
        const shift = para.align==='center' ? slack/2 : para.align==='right' ? slack : 0;
        for(const t of l.toks) t.dx += shift;
      }
      /* LINE HEIGHT IS A MULTIPLE OF THE TYPE SIZE, which is what the number
         in the inspector says it is and what it means everywhere else in
         typography. It used to be a multiple of the line's own measured ink,
         so 1.3 meant 1.3 of whatever those particular letters happened to
         occupy: a line with a descender sat further from the next one than a
         line without, and no two paragraphs leaded alike.

         The baseline is then placed by half-leading — the slack above and
         below the face's own extent, split evenly — which is what CSS does and
         what keeps the text optically centred in its line however loose or
         tight the leading is set. */
      const step = l.size * lh;
      const half = Math.max(0, (step - (l.asc + l.desc)) / 2);
      out.push({ toks: l.toks, marker: l.marker, y: y + half + l.asc, height: step });
      y += step;
    });
    y += baseSizePx * .45;          // gap between paragraphs
  }
  /* NUMBER THE WORDS, in reading order across the whole block — which is the
     order a reveal brings them in, and is not the order they are stored in:
     wrapping has already cut runs into lines, so a run's third word can sit
     on the next line from its second. Whitespace is not a word and takes no
     number, so "two words" means two, not five with the gaps counted.

     A list marker takes the number of the first word on its line, so the
     bullet arrives with the item it belongs to rather than ahead of it. */
  let wi = 0;
  for (const l of out) {
    if (l.marker) l.marker.wi = wi;
    for (const t of l.toks) if (!t.space && t.text) t.wi = wi++;
  }
  return { lines: out, height: Math.max(1, y), words: wi };
}

/* ── the word-by-word reveal ──────────────────────────────────────────────
   One number, 0 to 1, for how much of the block has arrived. It is an
   ordinary animatable property (see core/anim.js), so the reveal is keyframes
   on the ordinary timeline — scrubbable, editable, undoable — rather than a
   second clock running beside it.

   Each word fades rather than appearing, and the fades OVERLAP: word n is
   still coming up as n+1 starts, so at any instant two or three are in
   flight. A strictly sequential reveal reads as a stutter at signage sizes,
   because each word's arrival is its own discrete event; overlapping them
   turns a sequence of events into one continuous movement across the block. */
const REVEAL_OVERLAP = 2.5;   // each word's fade is this many word-steps long

/**
 * How visible word `index` of `total` is at a given reveal.
 *
 * The windows are laid out so that the LAST word finishes exactly at 1: with
 * n-1 steps before it and a fade of k steps, the step is 1/(n-1+k). Anything
 * else leaves the block either finishing early — with dead time on the end of
 * a move somebody set a length for — or still arriving after it should be
 * done, which is worse, since the export ends and the last word never lands.
 */
export function wordAlpha(index, total, reveal) {
  if (!(reveal < 1)) return 1;                 // the default, and the fast path
  if (!(reveal > 0)) return 0;
  if (total <= 0) return 1;
  const step = 1 / (total - 1 + REVEAL_OVERLAP);
  const u = (reveal - index * step) / (REVEAL_OVERLAP * step);
  const t = u <= 0 ? 0 : u >= 1 ? 1 : u;
  return t * t * (3 - 2 * t);                  // smoothstep, so the fade has no corners
}

/* ── THE OTHER EDGE ───────────────────────────────────────────────────────
   `reveal` says how much of the block has ARRIVED, counted from the first
   word. That is one edge of a wipe, and one edge can only ever express one
   exit: run it backwards and the LAST word goes first, because the last word
   is the one whose window sits at the top of the range. No value of a single
   number says "the first word has gone and the rest are still here".

   So there is a second, `erase`: how much has LEFT, counted from the same
   end and at the same cadence. A word is drawn where it has arrived and not
   yet left, which is the ordinary reading of two edges sweeping the same way.

   Between them the three exits are all reachable and none is a special case:
     erase 0 → 1     the sentence is consumed from its first word
     reveal 1 → 0    it un-writes itself from its last
     both at once    a band of words travelling across the block

   `erase` is 0 on every block nobody has touched, which is the inert value —
   so this costs nothing and changes nothing until it is animated. */

/** The block's own reveal, clamped. Absent means "all of it" — every block
 *  that has never been animated, which is nearly all of them. */
export const revealOf = o =>
  o?.params?.reveal == null ? 1 : clamp(+o.params.reveal || 0, 0, 1);

/** How much has left, from the same end. Absent means none of it, which is
 *  every block that has never been animated. */
export const eraseOf = o =>
  o?.params?.erase == null ? 0 : clamp(+o.params.erase || 0, 0, 1);

/**
 * Everything about a block that does NOT depend on the playhead: the wrapped
 * lines, the canvas size, where the content sits within it.
 *
 * Split from the painting because a word reveal repaints every frame, and the
 * measuring is the expensive half — every token goes through the canvas text
 * metrics API, so a wordy block re-measured at 30fps for the length of an
 * export is tens of thousands of calls all returning the same numbers. The
 * layout cannot change while only the reveal does: the words appear where
 * they were always going to be, which is the whole reason the box does not
 * reflow as they arrive.
 *
 * @returns {object|null} `cw`/`ch` are the canvas in pixels; `w`/`h` the plane
 *          in METRES — `w` the box width you set (plus outline/shadow/glow
 *          bleed, if any are on) and `h` derived from the content, a "fixed
 *          width, auto height" box.
 */
export function planTextArea(o){
  const P = o.params;
  if(!P.paragraphs || !P.paragraphs.length) return null;

  /* Text Area is never traced — the canvas IS the final image — so unlike 3D
     Text's fixed quality tiers, resolution here matches the deliverable's own
     pixel density. Downscaled uniformly (same idiom as buildTextEffects's
     FX_MAX_PX) only when that would blow past a sane texture size. */
  const density = S.res.w / Math.max(1e-6, S.screen.w);
  const rawWidthPx = Math.max(1, P.w * density);
  const rawBaseSizePx = Math.max(1, P.baseSize * density);
  const rawPaddingPx = Math.max(0, P.padding * density);

  const over = rawWidthPx / FX_MAX_PX;
  const k = over > 1 ? 1/over : 1;
  const widthPx = rawWidthPx * k, baseSizePx = rawBaseSizePx * k, paddingPx = rawPaddingPx * k;

  const innerWidth = Math.max(1, widthPx - paddingPx*2);
  const { lines, height, words } = layoutParagraphs(P.paragraphs, innerWidth, baseSizePx, P.lineHeight || 1.3);

  /* Outline/shadow/glow bleed outward past the box's own padding, the same
     idea buildTextEffects (scene/geometry.js) grows its own canvas by for
     3D Text's separate fx plane — a blur's visible falloff runs to roughly
     3x its radius, and a fat outline stroke extends past the glyph edge by
     half its width. Percentages of Base size here rather than of cap
     height, since a Text Area has no single cap height (see DEF.textarea).
     Zero when none of the three are on, so the canvas — and the plane it
     maps onto — comes out identically to before these existed. */
  const pct = v => Math.max(0, +v || 0) / 100;
  const bleedPct = Math.max(
    P.fxShadow ? pct(P.fxShadowBlur==null?6:P.fxShadowBlur)*3 + Math.max(Math.abs(+P.fxShadowDX||0),Math.abs(+P.fxShadowDY||0))/100 : 0,
    P.fxGlow   ? pct(P.fxGlowSize==null?12:P.fxGlowSize)*3 : 0,
    P.fxOutline ? pct(P.fxOutlineW==null?6:P.fxOutlineW)*0.6 : 0
  );
  const bleedPx = baseSizePx * bleedPct;

  const w = Math.max(2, Math.round(widthPx + bleedPx*2));
  const h = Math.max(2, Math.round(height + paddingPx*2 + bleedPx*2));

  return {
    lines, words,
    cw: w, ch: h,
    ox: paddingPx + bleedPx, oy: paddingPx + bleedPx,
    baseSizePx, pct,
    outlineW: P.fxOutline ? Math.max(1, baseSizePx*pct(P.fxOutlineW==null?6:P.fxOutlineW)) : 0,
    w: bleedPx>0 ? (w/k)/density : P.w,
    h: (h/k) / density
  };
}

/**
 * Draw a planned block into a canvas, at a given reveal.
 *
 * Repeatable: it clears first and holds no state between calls, so the same
 * canvas can be repainted frame after frame as a reveal runs. At reveal 1 —
 * which is every block nobody has animated — it takes the same path it always
 * did, so an untouched Text Area rasterises to the pixels it always has.
 */
export function paintTextArea(plan, o, cv, reveal = 1, erase = 0){
  const P = o.params;
  const { lines, words, ox, oy, baseSizePx, pct, outlineW } = plan;
  /* Only when it actually differs: assigning width resets the whole surface
     and every bit of context state with it, which on a repaint is throwing
     away the allocation we came here to reuse. */
  if(cv.width !== plan.cw || cv.height !== plan.ch){ cv.width = plan.cw; cv.height = plan.ch; }
  const c2 = cv.getContext('2d');
  c2.clearRect(0, 0, plan.cw, plan.ch);
  c2.textBaseline = 'alphabetic';

  /* One number per drawn thing, and 1 for everything when neither edge has
     moved — so the whole feature costs two comparisons on the common path,
     which is every block nobody has animated.

     Arrived AND not yet left. Multiplied rather than subtracted: the two
     edges are independent sweeps, and a word caught by both at once should
     be as faint as each of them says, not driven negative by the pair. */
  const full = !(reveal < 1) && !(erase > 0);
  const alphaOf = tok => {
    if(full) return 1;
    const i = tok.wi || 0;
    return wordAlpha(i, words, reveal) * (1 - wordAlpha(i, words, erase));
  };

  /* Glow, then shadow, then the fill (with its own outline) — back to
     front, same ordering rationale as buildTextEffects: glow hugs the
     letters, shadow reads as further back, and the outlined fill sits on
     top of both. Drawing a glyph with a shadow set also paints the glyph
     itself, which would read as a solid duplicate rather than a shadow —
     the standard canvas fix is to push the glyph far off-canvas and give
     the shadow an equal and opposite offset, so only the shadow lands in
     frame; see buildTextEffects for the same trick. */
  const paintPass = (color, blur, dx, dy, alpha, passes) => {
    const OFF = plan.cw + plan.ch + 1000;
    c2.save();
    c2.globalAlpha = alpha;
    c2.fillStyle = '#000';
    c2.shadowColor = color;
    c2.shadowBlur = blur;
    for(let i=0;i<passes;i++){
      c2.shadowOffsetX = dx + OFF;
      c2.shadowOffsetY = dy;
      for(const l of lines){
        /* A word's glow and shadow come up WITH it. Drawn at full strength
           under a word still fading in, they would arrive as a halo around
           nothing — the effect leading its own letters. */
        if(l.marker){
          c2.globalAlpha = alpha * alphaOf(l.marker);
          c2.font = l.marker.font; c2.fillText(l.marker.text, ox - OFF, oy + l.y);
        }
        for(const t of l.toks){
          if(t.space || !t.text) continue;
          const a = alphaOf(t);
          if(a <= 0) continue;
          c2.globalAlpha = alpha * a;
          c2.font = t.font; c2.fillText(t.text, (ox+t.dx) - OFF, oy+l.y);
        }
      }
    }
    c2.restore();
  };
  if(P.fxGlow) paintPass(P.fxGlowColor||'#F2C300', baseSizePx*pct(P.fxGlowSize==null?12:P.fxGlowSize), 0, 0,
                          clamp(P.fxGlowOpacity==null?.8:+P.fxGlowOpacity, 0, 1), 3);
  if(P.fxShadow) paintPass(P.fxShadowColor||'#000000', baseSizePx*pct(P.fxShadowBlur==null?6:P.fxShadowBlur),
                            baseSizePx*(+P.fxShadowDX||0)/100, -baseSizePx*(+P.fxShadowDY||0)/100,
                            clamp(P.fxShadowOpacity==null?.55:+P.fxShadowOpacity, 0, 1), 1);

  for(const l of lines){
    if(l.marker){
      c2.globalAlpha = alphaOf(l.marker);
      c2.font = l.marker.font;
      if(P.fxOutline){
        c2.strokeStyle = P.fxOutlineColor||'#000000'; c2.lineWidth = outlineW; c2.lineJoin = 'round';
        c2.strokeText(l.marker.text, ox, oy + l.y);
      }
      c2.fillStyle = l.marker.color;
      c2.fillText(l.marker.text, ox, oy + l.y);
    }
    for(const t of l.toks){
      if(t.space || !t.text) continue;
      /* A word not yet arrived is not drawn at all — cheaper than drawing it
         at zero, and it means the underline cannot show under a word that is
         not there yet. */
      const a = alphaOf(t);
      if(a <= 0) continue;
      c2.globalAlpha = a;
      const x = ox + t.dx, y = oy + l.y;
      c2.font = t.font;
      if(P.fxOutline){
        c2.strokeStyle = P.fxOutlineColor||'#000000'; c2.lineWidth = outlineW; c2.lineJoin = 'round';
        c2.strokeText(t.text, x, y);
      }
      c2.fillStyle = t.color;
      c2.fillText(t.text, x, y);
      if(t.underline){
        // canvas has no native underline any more than it has bold without a real weight
        c2.strokeStyle = t.color;
        c2.lineWidth = Math.max(1, baseSizePx*.045);
        const uy = y + baseSizePx*.12;
        c2.beginPath(); c2.moveTo(x, uy); c2.lineTo(x + t.width, uy); c2.stroke();
      }
    }
  }
  c2.globalAlpha = 1;
  return cv;
}

/**
 * Plan and paint in one go — the whole job for a block being built.
 *
 * @returns {{canvas:HTMLCanvasElement, w:number, h:number, plan:object}|null}
 *          `plan` is handed back so the caller can keep it and repaint the
 *          same canvas as a reveal runs, instead of measuring again.
 */
export function buildTextAreaTexture(o){
  const plan = planTextArea(o);
  if(!plan) return null;
  const canvas = paintTextArea(plan, o, document.createElement('canvas'), revealOf(o), eraseOf(o));
  return { canvas, w: plan.w, h: plan.h, plan };
}

/** Every distinct style a block uses, for the font-readiness gate — one entry
 *  per run rather than deduped, the same shape buildText's loop already walks
 *  for 3D Text's (now single-style) readiness check. */
export function textAreaStyles(paragraphs){
  const out = [];
  for(const p of (paragraphs||[])) for(const r of (p.runs||[])) if(r && r.text) out.push(r);
  return out;
}
